import {
    convertInchesToTwip,
    Document,
    ExternalHyperlink,
    Footer,
    Header,
    HorizontalPositionAlign,
    HorizontalPositionRelativeFrom,
    ImageRun,
    Paragraph,
    TabStopType,
    TextRun,
    VerticalPositionRelativeFrom
} from "docx";
import {
    croppedSynBackgroundImageBase64Data,
    synergistDocxFooterImageBase64Data,
    synergistDocxHeaderImageBase64Data
} from "../components/constants/docxBase64ImageData";

const formatDate = (date) => {
    return date.toLocaleDateString('en-us', { year:"numeric", month:"short"});
}

const generateDocx = (userInfo, skillBuckets, projectBuckets, userExperience, userDegrees, userCerts) => {
    const titleBorder = {
        top: {
            color: '#808080',
            space: 1,
            style: 'single',
            size: 2,
        },
        bottom: {
            color: '#808080',
            space: 1,
            style: 'single',
            size: 2,
        },
    };

    // skills
    const skillLines = [];
    Object.keys(skillBuckets).forEach((key) => {
        skillLines.push(new Paragraph({
            bullet: {
                level: 0,
            },
            children: [
                new TextRun({text: `${key}: `, bold: true, size: '9pt'}),
                new TextRun({text: `${skillBuckets[key].join(', ')}`, size: '9pt'})
            ],
            indent: {
                left: convertInchesToTwip(1.25), // bullet requires additional indentation
            },
        }));
    });

    // exp
    const expLines = [];
    userExperience.forEach((exp) => {
        expLines.push(new Paragraph({
            children: [
                new TextRun({text: `${exp.position}, `, bold: true, size: '10pt'}),
                new TextRun({text: `${exp.company}`, size: '10pt'}),
                new TextRun({
                    text: `\t${formatDate(exp.startDate)} - ${exp.isPresent ? 'Present' : formatDate(exp.endDate)}`,
                    size: '10pt'
                })
            ],
            indent: {
                left: convertInchesToTwip(1),
            },
            tabStops: [{
                type: TabStopType.RIGHT,
                position: convertInchesToTwip(7.25),
            }],
        }));
        const projCount = projectBuckets[exp.id].length;
        projectBuckets[exp.id].forEach((proj) => {
            if (projCount >= 2 && proj.projectName !== undefined) {
                expLines.push(new Paragraph({
                    children: [
                        new TextRun({
                            text: `${proj.projectName} - ${formatDate(proj.startDate)} - ${proj.isPresent ? 'Present' : formatDate(proj.endDate)}`,
                            font: "Calibri"
                        }),
                    ],
                    indent: {
                        left: convertInchesToTwip(1),
                    },
                }));
            }
            const responsibilityCount = proj.splitResponsibilities.length;
            const responsibilityTextRuns = proj.splitResponsibilities.map((respon) => {
                if (responsibilityCount > 1){
                    return new Paragraph({
                        bullet: {
                            level: 0,
                        },
                        children: [new TextRun({text: `${respon}`, size: '9pt'})],
                        indent: {
                            left: convertInchesToTwip(1.25), // bullet requires additional indentation
                        },
                    })
                }
                else {
                    return new Paragraph({
                        children: [new TextRun({text: `${respon}`, size: '9pt'})],
                        indent: {
                            left: convertInchesToTwip(1.25), // same indentation for effect with bullet
                        },
                    })
                }
            });
            responsibilityTextRuns.forEach((run) => {
                expLines.push(run);
            });
        });
        // put new line in between experience/company blocks
        expLines.push(new Paragraph({}));
    });

    //education
    const degreeLines = [];
    userDegrees.forEach((degree) => {
        degreeLines.push(new Paragraph({
            children: [
                new TextRun({text: `${degree.degreeName} ${degree.major}, `, bold: true, size: '10pt'}),
                new TextRun({text: `${degree.institution}`, size: '10pt'}),
                new TextRun({text: `\t${formatDate(degree.dateEarned)}`, size: '10pt'})
            ],
            indent: {
                left: convertInchesToTwip(1),
            },
            tabStops: [{
                type: TabStopType.RIGHT,
                position: convertInchesToTwip(7.25),
            }],
        }));
    });

    //certs
    const certLines = [];
    userCerts.forEach((cert) => {
        certLines.push(new Paragraph({
            children: [
                new TextRun({text: `${cert.certName}`, bold: true, size: '10pt'}),
                new TextRun({text: `\t${formatDate(cert.dateEarned)}`, size: '10pt'})
            ],
            indent: {
                left: convertInchesToTwip(1),
            },
            tabStops: [{
                type: TabStopType.RIGHT,
                position: convertInchesToTwip(7.25),
            }],
        }));
    });

    // dynamically set children here and then pass that in
    const documentChildren = [];
    documentChildren.push(
        new Paragraph({
            children: [
                new TextRun({text: `${userInfo.firstName} ${userInfo.lastName}`, bold: true, size: '13pt'}),
                new ExternalHyperlink({
                    children: [
                        new TextRun({text: 'info@synergistcomputing.com', break: 1, style: "Hyperlink"})
                    ],
                    link: 'mailto:info@synergistcomputing.com',
                }),
            ],
            indent: {
                left: convertInchesToTwip(1),
            },
            spacing: {
                after: 200,
            },
        })
    );

    if (skillLines.length > 0) {
        documentChildren.push(
            new Paragraph({
                border: titleBorder,
                children: [
                    new TextRun({text: "TECHNICAL EXPERTISE", bold: true, size: '9pt'}),
                ],
                indent: {
                    left: convertInchesToTwip(1),
                },
            })
        );

        skillLines.forEach((skillLine) => {
            documentChildren.push((skillLine));
        });
    }

    if (expLines.length > 0) {
        documentChildren.push(new Paragraph({}));
        documentChildren.push(
            new Paragraph({
                border: titleBorder,
                children: [
                    new TextRun({text: "PROFESSIONAL EXPERIENCE", bold: true, size: '9pt'}),
                ],
                indent: {
                    left: convertInchesToTwip(1),
                },
            })
        );
        expLines.forEach((expLine) => {
            documentChildren.push(expLine);
        });
    }


    if (degreeLines.length > 0) {
        documentChildren.push(new Paragraph({}));
        documentChildren.push(
            new Paragraph({
                border: titleBorder,
                children: [
                    new TextRun({text: "EDUCATION", bold: true, size: '9pt'}),
                ],
                indent: {
                    left: convertInchesToTwip(1),
                },
            })
        );
        degreeLines.forEach((degreeLine) => {
            documentChildren.push(degreeLine);
        });
    }

    if (certLines.length > 0) {
        documentChildren.push(new Paragraph({}));
        documentChildren.push(
            new Paragraph({
                border: titleBorder,
                children: [
                    new TextRun({text: "CERTIFICATIONS", bold: true, size: '9pt'}),
                ],
                indent: {
                    left: convertInchesToTwip(1),
                },
            })
        );
        certLines.forEach((certLine) => {
            documentChildren.push((certLine));
        });
    }

    return new Document({
        creator: "Synergist Resume Tool",
        description: `resume`,
        title: `${userInfo.lastName}-${userInfo.firstName}-${new Date().toISOString().split('T')[0]}`,
        styles: { // override default styling, this will allow us to not need custom numbering
            paragraphStyles: [ // bullet will now work as expected on ListParagraphs
                {
                    name: 'Normal',
                    run: {
                        font: 'Calibri',
                        size: '9pt',
                    }
                }
            ]
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: convertInchesToTwip(1.5),
                            bottom: convertInchesToTwip(1.25),
                            // left margin would affect header/footer, so we set this to zero
                            // and indent all of our paragraphs
                            left: convertInchesToTwip(0),
                            right: convertInchesToTwip(1),
                            header: convertInchesToTwip(0),
                            footer: convertInchesToTwip(0),
                        },
                    },
                },
                // width / height for header / footer is in pixels
                headers: {
                    default: new Header({
                        children: [
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        data: Buffer.from(synergistDocxHeaderImageBase64Data, "base64"),
                                        transformation: {
                                            width: 816,
                                            height: 100,
                                        },
                                    }),
                                ],
                            }),
                        ],
                    }),
                },
                footers: {
                    default: new Footer({
                        children: [
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        data: Buffer.from(croppedSynBackgroundImageBase64Data, "base64"),
                                        transformation: {
                                            width: 360, // original is 998x1432
                                            height: 516,
                                        },
                                        floating: {
                                            horizontalPosition: {
                                                relative: HorizontalPositionRelativeFrom.PAGE,
                                                align: HorizontalPositionAlign.RIGHT,
                                            },
                                            verticalPosition: {
                                                relative: VerticalPositionRelativeFrom.PAGE,
                                                offset: 5072000, // 1014400 x 5 (inch?) no idea what this unit is, it's not twip
                                            }
                                        }
                                    }),
                                ],
                            }),
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        data: Buffer.from(synergistDocxFooterImageBase64Data, "base64"),
                                        transformation: {
                                            width: 816,
                                            height: 50,
                                        },
                                    }),
                                ],
                            }),
                        ],
                    }),
                },
                children: documentChildren,
            },
        ],
    });
}

export {
    generateDocx,
}